import { defineStore } from 'pinia'
import { indicadorRespostaService } from '@/services/indicadorDeBemEstarResposta.service'
import { computed, reactive, ref } from 'vue'
import { i18N, toast } from '@/common/util'
import { cookies } from '@/common/cookies'

import type { IndicatorType, LoaderType, AnswerIndicatorType } from '@/types/types'
import useAuthStore from './authStore'

export const useIndicadorDeBemEstarRespostaStore = defineStore('indicadorDeBemEstarResposta', () => {
  const showIndicator = ref(false)
  const activeIndicator = ref<IndicatorType | null>(null)
  const status = reactive<Record<string, LoaderType>>({
    getAll: 'initial',
    create: 'initial',
    update: 'initial',
    delete: 'initial',
    getById: 'initial'
  })

  const loading = computed(() => Object.values(status).some(el => el === 'loading'))

  /**
   * Actions
   */
  const getActiveIndicator = async () => {
    // Ignora a ação para o modo cliente
    if (useAuthStore().clientMode.contract)
      return

    const now = new Date()

    const nextCheck = cookies.get<{ nextCheck: number }>('nextCheck')
    if (nextCheck && new Date(nextCheck.nextCheck) > now) {
      showIndicator.value = false
      activeIndicator.value = null
      return null
    }

    status.getById = 'loading'

    try {
      const { data } = await indicadorRespostaService.getActiveIndicator()

      if (data?.id) {
        cookies.add('activeIndicator', data, { expires: new Date(data.dataFim) })

        // Proxima verificação a partir da meia-noite do dia de término do indicador
        const dataFim = new Date(data.dataFim)
        dataFim.setHours(23, 59, 59, 999)
        cookies.add('nextCheck', { nextCheck: dataFim.getTime() }, { expires: dataFim })

        showIndicator.value = !!data
        activeIndicator.value = data
      } else {
        cookies.add('nextCheck', { nextCheck: Date.now() + 2 * 60 * 60 * 1000 }, { expires: new Date(Date.now() + 2 * 60 * 60 * 1000) })
        showIndicator.value = false
        activeIndicator.value = null
      }

      status.getById = 'success'
      return activeIndicator.value
    } catch {
      status.getById = 'error'
    }
  }

  const saveAnswerIndicator = async (id: string, objData: AnswerIndicatorType) => {
    status.create = 'loading'
    try {
      const { data, success } = await indicadorRespostaService.saveAnswerIndicator(id, objData)

      if (success) {
        toast.success({ message: i18N('toasts.sendAnswer') })
      }

      status.create = 'success'
      return data
    } catch {
      status.create = 'error'
    }
  }

  return {
    status,
    loading,
    showIndicator,
    activeIndicator,
    saveAnswerIndicator,
    getActiveIndicator
  }
})